/* Ronaq — 3D configurator scene (dining set / niche).
 * Exposes window.RonaqScene.create(host, mode) -> { update(opts), setHost(el), snapshot(), resetView() }
 * and fires `ronaq3d:ready` on window once loaded. Units are metres, Y is up. */
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.160.0/+esm';
import { OrbitControls } from 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/controls/OrbitControls.js/+esm';
import { RoomEnvironment } from 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/environments/RoomEnvironment.js/+esm';
import { RoundedBoxGeometry } from 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/geometries/RoundedBoxGeometry.js/+esm';

/* ---------------- helpers ---------------- */
const texCache = new Map();
const loader = new THREE.TextureLoader();
loader.setCrossOrigin('anonymous');

function loadTex(url, onLoad) {
  if (!url) return null;
  if (texCache.has(url)) return texCache.get(url);
  const t = loader.load(url, onLoad);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.MirroredRepeatWrapping;
  t.anisotropy = 8;
  texCache.set(url, t);
  return t;
}

/* fine upholstery weave, tinted by the fabric colour (photos of folded cloth tile badly) */
let weaveTex = null;
function weave() {
  if (weaveTex) return weaveTex;
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const x = c.getContext('2d');
  x.fillStyle = '#d9d9d9';
  x.fillRect(0, 0, 128, 128);
  for (let i = 0; i < 128; i += 4) {
    for (let j = 0; j < 128; j += 4) {
      const over = ((i + j) / 4) % 2 === 0;
      const v = 200 + Math.floor(Math.random() * 40) + (over ? 14 : -14);
      x.fillStyle = `rgb(${v},${v},${v})`;
      if (over) x.fillRect(i, j, 4, 3);
      else x.fillRect(i, j, 3, 4);
    }
  }
  weaveTex = new THREE.CanvasTexture(c);
  weaveTex.wrapS = weaveTex.wrapT = THREE.RepeatWrapping;
  weaveTex.repeat.set(1 / 0.035, 1 / 0.035);
  weaveTex.colorSpace = THREE.SRGBColorSpace;
  weaveTex.anisotropy = 8;
  return weaveTex;
}

/* neutral wood grain: a real wood photo turned grey and evened out, then tinted with the chosen finish */
const grainCache = new Map();
function grain(url, onLoad) {
  if (!url) return null;
  if (grainCache.has(url)) return grainCache.get(url);
  const c = document.createElement('canvas');
  c.width = c.height = 512;
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.MirroredRepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    const x = c.getContext('2d', { willReadFrequently: true });
    x.drawImage(img, 0, 0, 512, 512);
    const d = x.getImageData(0, 0, 512, 512);
    const px = d.data;
    let sum = 0;
    for (let i = 0; i < px.length; i += 4) sum += px[i] * 0.3 + px[i + 1] * 0.59 + px[i + 2] * 0.11;
    const mean = sum / (px.length / 4);
    for (let i = 0; i < px.length; i += 4) {
      const l = px[i] * 0.3 + px[i + 1] * 0.59 + px[i + 2] * 0.11;
      const v = Math.max(120, Math.min(255, 205 + (l - mean) * 1.25));
      px[i] = px[i + 1] = px[i + 2] = v;
    }
    x.putImageData(d, 0, 0);
    tex.needsUpdate = true;
    if (onLoad) onLoad();
  };
  img.src = url;
  grainCache.set(url, tex);
  return tex;
}

/* UVs in metres, projected along each vertex's dominant normal axis (box-mapping),
 * so every part shares one texture density and nothing looks stretched. */
function boxUV(geo, scale = 1) {
  const pos = geo.attributes.position;
  const nor = geo.attributes.normal;
  if (!nor) geo.computeVertexNormals();
  const n = geo.attributes.normal;
  const uv = new Float32Array(pos.count * 2);
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    const ax = Math.abs(n.getX(i)), ay = Math.abs(n.getY(i)), az = Math.abs(n.getZ(i));
    let u, v;
    if (ay >= ax && ay >= az) (u = x), (v = z);
    else if (ax >= az) (u = z), (v = y);
    else (u = x), (v = y);
    uv[i * 2] = u / scale;
    uv[i * 2 + 1] = v / scale;
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  return geo;
}

function mesh(geo, mat, { x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0, shadow = true, uv = true } = {}) {
  if (uv) boxUV(geo);
  const m = new THREE.Mesh(geo, mat);
  m.position.set(x, y, z);
  m.rotation.set(rx, ry, rz);
  m.castShadow = shadow;
  m.receiveShadow = true;
  return m;
}
const rbox = (w, h, d, r = 0.008, seg = 3) => new RoundedBoxGeometry(w, h, d, seg, Math.min(r, w / 2, h / 2, d / 2) * 0.999);
function extrude(shape, depth, bevel = 0.006) {
  const g = new THREE.ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: bevel > 0,
    bevelThickness: bevel,
    bevelSize: bevel,
    bevelSegments: 3,
    curveSegments: 36,
  });
  g.translate(0, 0, -depth / 2);
  return g;
}
function lathe(points, scaleR = 1, scaleY = 1, seg = 28) {
  return new THREE.LatheGeometry(points.map(([r, y]) => new THREE.Vector2(r * scaleR, y * scaleY)), seg);
}

/* turned leg profile (radius, height) — height normalised to 1 */
const TURNED = [[0.0, 0], [0.6, 0], [0.55, 0.06], [0.75, 0.1], [0.5, 0.16], [0.42, 0.45], [0.55, 0.62], [0.8, 0.68], [0.6, 0.74], [0.75, 0.86], [1, 0.9], [1, 1], [0, 1]];
const PEDESTAL = [[0, 0], [0.34, 0], [0.35, 0.02], [0.3, 0.04], [0.16, 0.08], [0.1, 0.16], [0.075, 0.24], [0.1, 0.36], [0.08, 0.46], [0.062, 0.56], [0.11, 0.66], [0.16, 0.69], [0.16, 0.7], [0, 0.7]];
const CUP = [[0, 0], [0.025, 0], [0.027, 0.004], [0.03, 0.01], [0.042, 0.05], [0.044, 0.07], [0.041, 0.07], [0.039, 0.05], [0.027, 0.012], [0, 0.012]];

/* ---------------- materials (mutated in place when options change) ---------------- */
function makeMaterials() {
  return {
    wood: new THREE.MeshPhysicalMaterial({ color: 0x5b3a24, roughness: 0.48, clearcoat: 0.35, clearcoatRoughness: 0.35 }),
    woodDark: new THREE.MeshPhysicalMaterial({ color: 0x3a2417, roughness: 0.55 }),
    fabric: new THREE.MeshPhysicalMaterial({ color: 0xd8c6a8, roughness: 0.92, sheen: 1, sheenRoughness: 0.55, sheenColor: 0xffffff }),
    fabricDark: new THREE.MeshPhysicalMaterial({ color: 0x8a7a60, roughness: 0.8 }),
    top: new THREE.MeshPhysicalMaterial({ color: 0xffffff, roughness: 0.4, clearcoat: 0.6, clearcoatRoughness: 0.15 }),
    accent: new THREE.MeshStandardMaterial({ color: 0xc4a46b, metalness: 1, roughness: 0.28 }),
    handle: new THREE.MeshStandardMaterial({ color: 0xc4a46b, metalness: 1, roughness: 0.3 }),
    glass: new THREE.MeshPhysicalMaterial({
      color: 0xdfeef0, metalness: 0, roughness: 0.02, transparent: true, opacity: 0.16, clearcoat: 1, clearcoatRoughness: 0, depthWrite: false,
    }),
    porcelain: new THREE.MeshPhysicalMaterial({ color: 0xf6f2ea, roughness: 0.18, clearcoat: 1, clearcoatRoughness: 0.08 }),
    gold: new THREE.MeshStandardMaterial({ color: 0xc9a14a, metalness: 1, roughness: 0.25 }),
    interior: new THREE.MeshStandardMaterial({ color: 0x3a2417, roughness: 0.7, emissive: 0x000000 }),
    blackMetal: new THREE.MeshStandardMaterial({ color: 0x1b1b1b, metalness: 0.9, roughness: 0.35 }),
  };
}

function applyWood(M, w, redraw, grainUrl) {
  const custom = loadTex(w && w.map, redraw);
  const map = custom || grain(grainUrl, redraw);
  if (map) map.repeat.set(1 / 0.6, 1 / 0.6);
  // grain averages ~80% grey, so lift the finish colour to land on the swatch colour
  const col = new THREE.Color((w && w.color) || '#5b3a24');
  const tint = custom ? new THREE.Color(0xffffff) : col.clone().multiplyScalar(map ? 1.02 : 1);
  M.wood.map = map;
  M.wood.color.copy(tint);
  M.wood.needsUpdate = true;
  M.woodDark.map = map;
  M.woodDark.color.copy(tint).multiplyScalar(0.72);
  M.woodDark.needsUpdate = true;
  // the inside of a cabinet is the same wood, a little darker
  M.interior.map = map;
  M.interior.color.copy(tint).multiplyScalar(0.6);
  M.interior.needsUpdate = true;
  M.woodTint = tint;
  M.woodMap = map;
}
function applyFabric(M, f, redraw) {
  const custom = loadTex(f && f.map, redraw);
  if (custom) custom.repeat.set(1 / 0.22, 1 / 0.22);
  const map = custom || weave();
  const col = new THREE.Color((f && f.color) || '#d8c6a8');
  M.fabric.map = map;
  M.fabric.bumpMap = custom ? null : weave();
  M.fabric.bumpScale = 0.4;
  M.fabric.color.set(custom ? 0xffffff : col);
  M.fabric.sheenColor.copy(col).lerp(new THREE.Color(0xffffff), 0.22);
  M.fabric.sheen = 0.7;
  M.fabric.needsUpdate = true;
  M.fabricDark.map = custom;
  M.fabricDark.color.set(custom ? 0x6a6a6a : col.clone().multiplyScalar(0.55));
  M.fabricDark.needsUpdate = true;
}
function applyTop(M, t, wood, redraw) {
  const type = (t && t.type) || 'wood';
  const url = t && t.map;
  // a wooden top is the same wood as the rest of the set
  const map = type === 'glass' ? null : url ? loadTex(url, redraw) : type === 'wood' ? M.woodMap : null;
  if (map && url) map.repeat.set(1 / 0.9, 1 / 0.9);
  const m = M.top;
  m.map = map;
  m.transmission = type === 'glass' ? 0.95 : 0;
  m.transparent = type === 'glass';
  m.thickness = type === 'glass' ? 0.02 : 0;
  m.ior = 1.5;
  m.roughness = type === 'glass' ? 0.03 : type === 'wood' ? 0.4 : 0.12;
  m.clearcoat = type === 'wood' ? 0.6 : 1;
  m.clearcoatRoughness = type === 'wood' ? 0.2 : 0.05;
  const fallback = { wood: (wood && wood.color) || '#5b3a24', marble: '#f2efe9', black: '#1c1a19', glass: '#e6f2f1' }[type];
  if (type === 'wood' && !url && M.woodTint) m.color.copy(M.woodTint);
  else m.color.set(map ? 0xffffff : fallback);
  m.needsUpdate = true;
}
function applyMetal(mat, color, fallback) {
  mat.color.set(color || fallback);
  mat.needsUpdate = true;
}

/* ---------------- dining: chairs ---------------- */
const SEAT_Y = 0.47;
function chair(style, M, accent) {
  const g = new THREE.Group();
  const legMat = style === 'modern' ? M.blackMetal : M.wood;
  const legH = SEAT_Y - 0.06;
  const lx = 0.205, lz = 0.2;
  [[-lx, lz], [lx, lz], [-lx, -lz], [lx, -lz]].forEach(([x, z]) => {
    let geo;
    if (style === 'modern') geo = new THREE.CylinderGeometry(0.011, 0.009, legH, 14);
    else if (style === 'classic') geo = lathe(TURNED, 0.024, legH);
    else geo = new THREE.CylinderGeometry(0.02, 0.014, legH, 16);
    const m = mesh(geo, legMat, { x, y: style === 'classic' ? 0 : legH / 2, z });
    if (style === 'oval' || style === 'royal') m.rotation.set(z > 0 ? 0.06 : -0.08, 0, x > 0 ? -0.04 : 0.04);
    g.add(m);
  });
  // seat frame + cushion
  if (style !== 'modern') g.add(mesh(rbox(0.5, 0.055, 0.5, 0.01), M.wood, { y: SEAT_Y - 0.03 }));
  else g.add(mesh(rbox(0.46, 0.02, 0.46, 0.006), M.blackMetal, { y: SEAT_Y - 0.04 }));
  g.add(mesh(rbox(0.48, style === 'modern' ? 0.1 : 0.08, 0.48, 0.035, 4), M.fabric, { y: SEAT_Y + 0.03 }));
  if (accent) g.add(mesh(new THREE.BoxGeometry(0.5, 0.01, 0.004), M.accent, { y: SEAT_Y - 0.03, z: 0.252, shadow: false }));

  // back (built upright, then leaned back around the seat line)
  const back = new THREE.Group();
  back.position.set(0, SEAT_Y + 0.02, -0.22);
  back.rotation.x = -0.1;
  const tuft = (w, h, y0, rows, cols) => {
    for (let r = 0; r < rows; r++)
      for (let c = 0; c < cols - (r % 2); c++) {
        const x = -((cols - 1) / 2) * w + c * w + (r % 2 ? w / 2 : 0);
        back.add(mesh(new THREE.SphereGeometry(0.008, 10, 8), M.fabricDark, { x, y: y0 + r * h, z: 0.03, shadow: false }));
      }
  };
  if (style === 'classic') {
    back.add(mesh(rbox(0.46, 0.64, 0.05, 0.02), M.wood, { y: 0.34 }));
    back.add(mesh(rbox(0.38, 0.54, 0.05, 0.03, 4), M.fabric, { y: 0.34, z: 0.012 }));
    tuft(0.09, 0.12, 0.16, 4, 4);
    if (accent) back.add(mesh(rbox(0.47, 0.012, 0.055, 0.004), M.accent, { y: 0.66, shadow: false }));
  } else if (style === 'royal') {
    const arch = (w, h) => {
      const s = new THREE.Shape();
      s.moveTo(-w / 2, 0);
      s.lineTo(-w / 2, h * 0.62);
      s.bezierCurveTo(-w / 2, h * 0.92, -w * 0.2, h * 0.94, 0, h);
      s.bezierCurveTo(w * 0.2, h * 0.94, w / 2, h * 0.92, w / 2, h * 0.62);
      s.lineTo(w / 2, 0);
      s.closePath();
      return s;
    };
    back.add(mesh(extrude(arch(0.5, 0.78), 0.04), M.wood, { y: 0.02 }));
    back.add(mesh(extrude(arch(0.4, 0.66), 0.04, 0.012), M.fabric, { y: 0.07, z: 0.012 }));
    tuft(0.085, 0.12, 0.18, 4, 4);
    back.add(mesh(new THREE.SphereGeometry(0.03, 20, 14), accent ? M.accent : M.wood, { y: 0.83 }));
  } else if (style === 'modern') {
    const s = new THREE.Shape();
    const w = 0.46, h = 0.44, r = 0.12;
    s.moveTo(-w / 2, 0);
    s.lineTo(-w / 2, h - r);
    s.quadraticCurveTo(-w / 2, h, -w / 2 + r, h);
    s.lineTo(w / 2 - r, h);
    s.quadraticCurveTo(w / 2, h, w / 2, h - r);
    s.lineTo(w / 2, 0);
    s.closePath();
    back.add(mesh(extrude(s, 0.06, 0.02), M.fabric, { y: 0.06 }));
    for (let i = -1; i <= 1; i++) back.add(mesh(new THREE.BoxGeometry(0.004, 0.36, 0.004), M.fabricDark, { x: i * 0.12, y: 0.27, z: 0.052, shadow: false }));
    if (accent) back.add(mesh(new THREE.BoxGeometry(0.4, 0.012, 0.004), M.accent, { y: 0.07, z: -0.052, shadow: false }));
  } else {
    // Louis: oval medallion on two posts
    const rx = 0.2, ry = 0.25, f = 0.035;
    const ring = new THREE.Shape();
    ring.absellipse(0, 0, rx, ry, 0, Math.PI * 2);
    const hole = new THREE.Path();
    hole.absellipse(0, 0, rx - f, ry - f, 0, Math.PI * 2, true);
    ring.holes.push(hole);
    back.add(mesh(extrude(ring, 0.035), M.wood, { y: 0.42 }));
    const inner = new THREE.Shape();
    inner.absellipse(0, 0, rx - f, ry - f, 0, Math.PI * 2);
    back.add(mesh(extrude(inner, 0.035, 0.012), M.fabric, { y: 0.42, z: 0.006 }));
    back.add(mesh(new THREE.SphereGeometry(0.009, 10, 8), M.fabricDark, { y: 0.42, z: 0.03, shadow: false }));
    [-0.15, 0.15].forEach((x) => back.add(mesh(new THREE.CylinderGeometry(0.014, 0.016, 0.22, 12), M.wood, { x, y: 0.08 })));
    back.add(mesh(new THREE.SphereGeometry(0.022, 16, 12), accent ? M.accent : M.wood, { y: 0.69 }));
  }
  g.add(back);
  return g;
}

/* ---------------- dining: table ---------------- */
const TOP_Y = 0.76;
function table(o, M, accent) {
  const g = new THREE.Group();
  const seats = o.seats;
  const T = 0.045;
  let L, W, legs = [];
  if (o.shape === 'round') {
    L = W = { 4: 1.15, 6: 1.35, 8: 1.55, 10: 1.75 }[seats] || 0.9 + seats * 0.08;
    const top = new THREE.CylinderGeometry(L / 2, L / 2, T, 96);
    g.add(mesh(top, M.top, { y: TOP_Y - T / 2 }));
    g.add(mesh(new THREE.TorusGeometry(L / 2 - 0.004, 0.012, 10, 120), M.wood, { y: TOP_Y - T, rx: Math.PI / 2 }));
    legs.push(0);
  } else {
    L = o.shape === 'oval' ? { 4: 1.6, 6: 2.0, 8: 2.45, 10: 2.9 }[seats] || 2 : { 4: 1.4, 6: 1.9, 8: 2.4, 10: 2.9 }[seats] || 1.9;
    W = o.shape === 'oval' ? 1.05 : 0.98;
    if (o.shape === 'oval') {
      const s = new THREE.Shape();
      s.absellipse(0, 0, L / 2, W / 2, 0, Math.PI * 2);
      g.add(mesh(extrude(s, T - 0.012, 0.006), M.top, { y: TOP_Y - T / 2, rx: -Math.PI / 2 }));
      const apron = new THREE.Shape();
      apron.absellipse(0, 0, L / 2 - 0.12, W / 2 - 0.12, 0, Math.PI * 2);
      const ah = new THREE.Path();
      ah.absellipse(0, 0, L / 2 - 0.15, W / 2 - 0.15, 0, Math.PI * 2, true);
      apron.holes.push(ah);
      g.add(mesh(extrude(apron, 0.08, 0), M.wood, { y: TOP_Y - T - 0.04, rx: -Math.PI / 2 }));
      legs = L > 2.2 ? [-L * 0.27, L * 0.27] : [0];
    } else {
      g.add(mesh(rbox(L, T, W, 0.012), M.top, { y: TOP_Y - T / 2 }));
      g.add(mesh(new THREE.BoxGeometry(L - 0.16, 0.09, W - 0.16), M.wood, { y: TOP_Y - T - 0.045 }));
      if (accent) {
        g.add(mesh(new THREE.BoxGeometry(L - 0.155, 0.012, 0.004), M.accent, { y: TOP_Y - T - 0.07, z: W / 2 - 0.078, shadow: false }));
        g.add(mesh(new THREE.BoxGeometry(L - 0.155, 0.012, 0.004), M.accent, { y: TOP_Y - T - 0.07, z: -(W / 2 - 0.078), shadow: false }));
      }
      const lh = TOP_Y - T - 0.005;
      [[-1, -1], [1, -1], [-1, 1], [1, 1]].forEach(([sx, sz]) => {
        g.add(mesh(lathe(TURNED, 0.045, lh), M.wood, { x: sx * (L / 2 - 0.12), z: sz * (W / 2 - 0.12) }));
        if (accent) g.add(mesh(new THREE.CylinderGeometry(0.036, 0.036, 0.012, 20), M.accent, { x: sx * (L / 2 - 0.12), y: 0.03, z: sz * (W / 2 - 0.12), shadow: false }));
      });
    }
  }
  legs.forEach((x) => {
    g.add(mesh(lathe(PEDESTAL, 1.05, (TOP_Y - T) / 0.7, 40), M.wood, { x }));
    if (accent) g.add(mesh(new THREE.TorusGeometry(0.11, 0.008, 10, 48), M.accent, { x, y: 0.12, rx: Math.PI / 2, shadow: false }));
  });
  return { group: g, L, W };
}

function diningSet(o, M) {
  const root = new THREE.Group();
  const accent = !!o.accent;
  const { group, L, W } = table(o, M, accent);
  root.add(group);
  const place = (x, z, ry) => {
    const c = chair(o.chair, M, accent);
    c.position.set(x, 0, z);
    c.rotation.y = ry;
    root.add(c);
  };
  const n = o.seats;
  if (o.shape === 'round') {
    const r = L / 2 + 0.2;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + Math.PI / n;
      place(Math.sin(a) * r, Math.cos(a) * r, a + Math.PI);
    }
  } else {
    const ends = n >= 6 ? 2 : 0;
    const per = Math.ceil((n - ends) / 2);
    const span = L - (ends ? 0.5 : 0.3);
    for (let i = 0; i < per; i++) {
      const x = -span / 2 + (span / per) * (i + 0.5);
      let edge = W / 2;
      if (o.shape === 'oval') edge = (W / 2) * Math.sqrt(Math.max(0.15, 1 - (x / (L / 2)) ** 2));
      place(x, edge + 0.2, Math.PI);
      place(x, -(edge + 0.2), 0);
    }
    if (ends) {
      place(L / 2 + 0.2, 0, -Math.PI / 2);
      place(-(L / 2 + 0.2), 0, Math.PI / 2);
    }
  }
  return root;
}

/* ---------------- niche (china cabinet) ---------------- */
function niche(o, M) {
  const root = new THREE.Group();
  const m = Math.max(2, Math.min(5, o.modules || 3));
  const W = { 2: 1.4, 3: 1.8, 4: 2.2, 5: 2.6 }[m];
  const mw = W / m;
  const D = 0.48;
  const baseTop = 0.88;
  const hutchH = 1.14;
  const hD = 0.36;
  const hz = -(D - hD) / 2 - 0.02;
  const hy0 = baseTop + 0.035;
  const hy1 = hy0 + hutchH;

  // feet + plinth
  [[-1, -1], [1, -1], [-1, 1], [1, 1]].forEach(([sx, sz]) =>
    root.add(mesh(lathe([[0, 0], [0.6, 0], [1, 0.4], [0.8, 1], [0, 1]], 0.035, 0.07, 18), M.woodDark, { x: sx * (W / 2 - 0.06), z: sz * (D / 2 - 0.06) }))
  );
  root.add(mesh(rbox(W, 0.05, D, 0.006), M.wood, { y: 0.095 }));
  // carcass
  root.add(mesh(new THREE.BoxGeometry(W - 0.02, baseTop - 0.12, D - 0.02), M.wood, { y: 0.12 + (baseTop - 0.12) / 2 }));
  for (let i = 0; i < m; i++) {
    const cx = -W / 2 + mw * (i + 0.5);
    // drawer
    root.add(mesh(rbox(mw - 0.025, 0.15, 0.024, 0.006), M.wood, { x: cx, y: baseTop - 0.1, z: D / 2 }));
    root.add(mesh(new THREE.CylinderGeometry(0.007, 0.007, 0.13, 12), M.handle, { x: cx, y: baseTop - 0.1, z: D / 2 + 0.03, rz: Math.PI / 2 }));
    // door with raised panel
    const dh = baseTop - 0.36;
    root.add(mesh(rbox(mw - 0.025, dh, 0.024, 0.006), M.wood, { x: cx, y: 0.15 + dh / 2, z: D / 2 }));
    root.add(mesh(rbox(mw - 0.12, dh - 0.12, 0.02, 0.012), M.wood, { x: cx, y: 0.15 + dh / 2, z: D / 2 + 0.016 }));
    const kx = i % 2 === 0 ? cx + mw / 2 - 0.05 : cx - mw / 2 + 0.05;
    root.add(mesh(new THREE.SphereGeometry(0.016, 18, 12), M.handle, { x: kx, y: 0.15 + dh * 0.62, z: D / 2 + 0.028 }));
  }
  // top slab
  root.add(mesh(rbox(W + 0.06, 0.035, D + 0.04, 0.008), M.wood, { y: baseTop + 0.0175 }));

  // hutch: back, sides, roof, shelves
  root.add(mesh(new THREE.BoxGeometry(W - 0.04, hutchH, 0.02), o.light ? M.interior : M.interior, { y: (hy0 + hy1) / 2, z: hz - hD / 2 + 0.01 }));
  [-1, 1].forEach((sx) => root.add(mesh(new THREE.BoxGeometry(0.035, hutchH, hD), M.wood, { x: sx * (W / 2 - 0.0175), y: (hy0 + hy1) / 2, z: hz })));
  root.add(mesh(new THREE.BoxGeometry(W, 0.035, hD), M.wood, { y: hy1 - 0.0175, z: hz }));
  for (let i = 1; i < m; i++) root.add(mesh(new THREE.BoxGeometry(0.03, hutchH, hD - 0.03), M.wood, { x: -W / 2 + mw * i, y: (hy0 + hy1) / 2, z: hz - 0.015 }));
  const shelves = [hy0 + hutchH * 0.36, hy0 + hutchH * 0.68];
  shelves.forEach((y) => root.add(mesh(new THREE.BoxGeometry(W - 0.06, 0.018, hD - 0.05), M.wood, { y, z: hz - 0.02 })));

  // dishes
  const plate = (x, y, z, r) => {
    root.add(mesh(new THREE.CylinderGeometry(r, r * 0.82, 0.012, 40), M.porcelain, { x, y: y + r, z, rx: Math.PI / 2 - 0.14, uv: false }));
    root.add(mesh(new THREE.TorusGeometry(r * 0.9, 0.0025, 6, 48), M.gold, { x, y: y + r, z: z + 0.007, rx: -0.14, shadow: false, uv: false }));
  };
  const cup = (x, y, z) => root.add(mesh(lathe(CUP, 1, 1.4, 24), M.porcelain, { x, y, z, uv: false }));
  [hy0, ...shelves.map((s) => s + 0.009)].forEach((y, si) => {
    for (let i = 0; i < m; i++) {
      const cx = -W / 2 + mw * (i + 0.5);
      const z = hz - hD / 2 + 0.06;
      if ((i + si) % 2 === 0) {
        plate(cx - mw * 0.18, y, z, 0.1);
        plate(cx + mw * 0.12, y, z + 0.01, 0.085);
      } else {
        cup(cx - mw * 0.2, y, z + 0.06);
        cup(cx, y, z + 0.06);
        cup(cx + mw * 0.2, y, z + 0.06);
        plate(cx, y, z - 0.01, 0.08);
      }
    }
  });

  // glass doors
  const fz = hz + hD / 2 + 0.012;
  const dhh = hutchH - 0.05;
  for (let i = 0; i < m; i++) {
    const cx = -W / 2 + mw * (i + 0.5);
    const dw = mw - 0.02;
    const f = 0.045;
    const y = (hy0 + hy1) / 2;
    root.add(mesh(new THREE.BoxGeometry(dw, f, 0.024), M.wood, { x: cx, y: y + dhh / 2 - f / 2, z: fz }));
    root.add(mesh(new THREE.BoxGeometry(dw, f, 0.024), M.wood, { x: cx, y: y - dhh / 2 + f / 2, z: fz }));
    root.add(mesh(new THREE.BoxGeometry(f, dhh, 0.024), M.wood, { x: cx - dw / 2 + f / 2, y, z: fz }));
    root.add(mesh(new THREE.BoxGeometry(f, dhh, 0.024), M.wood, { x: cx + dw / 2 - f / 2, y, z: fz }));
    const pane = mesh(new THREE.BoxGeometry(dw - f * 2 + 0.01, dhh - f * 2 + 0.01, 0.006), M.glass, { x: cx, y, z: fz, shadow: false, uv: false });
    pane.receiveShadow = false;
    root.add(pane);
    const hx = i % 2 === 0 ? cx + dw / 2 - f / 2 : cx - dw / 2 + f / 2;
    root.add(mesh(new THREE.CylinderGeometry(0.006, 0.006, 0.12, 12), M.handle, { x: hx, y: y - 0.05, z: fz + 0.025 }));
  }

  // crown
  const cornice = (w, h, d, y) => root.add(mesh(rbox(w, h, d, 0.01), M.wood, { y, z: hz }));
  cornice(W + 0.1, 0.05, hD + 0.08, hy1 + 0.025);
  cornice(W + 0.05, 0.03, hD + 0.04, hy1 + 0.065);
  if (o.crown === 'arch' || o.crown === 'crown') {
    const a = o.crown === 'arch' ? W * 0.32 : W * 0.24;
    const h = o.crown === 'arch' ? 0.2 : 0.3;
    const s = new THREE.Shape();
    if (o.crown === 'arch') {
      s.moveTo(-a, 0);
      s.quadraticCurveTo(0, h * 2, a, 0);
      s.closePath();
    } else {
      s.moveTo(-a, 0);
      s.bezierCurveTo(-a * 0.9, h * 0.55, -a * 0.45, h * 0.15, -a * 0.28, h * 0.62);
      s.bezierCurveTo(-a * 0.16, h * 0.98, -0.02, h * 0.9, 0, h);
      s.bezierCurveTo(0.02, h * 0.9, a * 0.16, h * 0.98, a * 0.28, h * 0.62);
      s.bezierCurveTo(a * 0.45, h * 0.15, a * 0.9, h * 0.55, a, 0);
      s.closePath();
    }
    root.add(mesh(extrude(s, 0.05, 0.008), M.wood, { y: hy1 + 0.08, z: hz + hD / 2 + 0.01 }));
    if (o.crown === 'crown') {
      root.add(mesh(new THREE.SphereGeometry(0.035, 24, 16), M.handle, { y: hy1 + 0.08 + h + 0.03, z: hz + hD / 2 + 0.01 }));
      root.add(mesh(new THREE.BoxGeometry(W + 0.1, 0.012, 0.006), M.handle, { y: hy1 + 0.04, z: hz + (hD + 0.08) / 2 + 0.002, shadow: false }));
      root.add(mesh(new THREE.BoxGeometry(W + 0.06, 0.01, 0.006), M.handle, { y: baseTop + 0.0175, z: (D + 0.04) / 2 + 0.002, shadow: false }));
    }
  }

  // interior lighting
  if (o.light) {
    M.interior.emissive.set(0x6b4420);
    M.interior.emissiveIntensity = 0.9;
    for (let i = 0; i < m; i++) {
      const cx = -W / 2 + mw * (i + 0.5);
      [hy1 - 0.06, shelves[1] - 0.03, shelves[0] - 0.03].forEach((y, k) => {
        const p = new THREE.PointLight(0xffc982, k === 0 ? 0.55 : 0.4, 0.75, 1.6);
        p.position.set(cx, y, hz);
        root.add(p);
      });
      root.add(mesh(new THREE.BoxGeometry(mw - 0.08, 0.006, 0.02), new THREE.MeshBasicMaterial({ color: 0xfff1d0 }), { x: cx, y: hy1 - 0.04, z: hz, shadow: false, uv: false }));
    }
  } else {
    M.interior.emissive.set(0x000000);
  }
  M.interior.needsUpdate = true;
  return root;
}

/* ---------------- scene ---------------- */
function create(host, mode, assets = {}) {
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.domElement.className = 'rq-3d-canvas';

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(renderer), 0.04).texture;
  scene.environmentIntensity = 0.75;

  const camera = new THREE.PerspectiveCamera(32, 16 / 10, 0.05, 60);
  const key = new THREE.DirectionalLight(0xfff3e2, 2.1);
  key.position.set(-2.5, 5, 3.5);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.camera.left = key.shadow.camera.bottom = -3.2;
  key.shadow.camera.right = key.shadow.camera.top = 3.2;
  key.shadow.radius = 6;
  key.shadow.bias = -0.0004;
  key.shadow.normalBias = 0.02;
  scene.add(key);
  scene.add(new THREE.HemisphereLight(0xfff6ea, 0x5b3a24, 0.35));

  const floor = new THREE.Mesh(new THREE.CircleGeometry(8, 64), new THREE.ShadowMaterial({ opacity: 0.28 }));
  floor.rotation.x = -Math.PI / 2;
  floor.receiveShadow = true;
  scene.add(floor);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.enablePan = false;
  controls.minPolarAngle = 0.35;
  controls.maxPolarAngle = 1.42;
  controls.rotateSpeed = 0.7;
  // phones: vertical swipes keep scrolling the page, sideways swipes turn the model
  renderer.domElement.style.touchAction = 'pan-y';

  const M = makeMaterials();
  let model = null;
  let structKey = '';
  let dirty = true;
  const redraw = () => (dirty = true);
  controls.addEventListener('change', redraw);

  const dir = mode === 'niche' ? new THREE.Vector3(0.38, 0.22, 1).normalize() : new THREE.Vector3(0.55, 0.62, 1).normalize();
  let fitDist = 4;
  function frame(keepAngle) {
    const box = new THREE.Box3().setFromObject(model);
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    const target = mode === 'niche' ? new THREE.Vector3(0, sphere.center.y * 0.98, 0) : new THREE.Vector3(0, 0.45, 0);
    const vfov = THREE.MathUtils.degToRad(camera.fov);
    const hfov = 2 * Math.atan(Math.tan(vfov / 2) * camera.aspect);
    const r = sphere.radius * (mode === 'niche' ? 0.92 : 0.86);
    fitDist = r / Math.sin(Math.min(vfov, hfov) / 2);
    controls.minDistance = fitDist * 0.55;
    controls.maxDistance = fitDist * 1.6;
    const d = keepAngle ? camera.position.clone().sub(controls.target).normalize() : dir.clone();
    controls.target.copy(target);
    camera.position.copy(target).addScaledVector(d, fitDist);
    controls.update();
    redraw();
  }

  function dispose(obj) {
    obj.traverse((n) => n.geometry && n.geometry.dispose());
  }

  function update(o) {
    if (mode === 'niche') {
      applyWood(M, o.wood, redraw, assets.grain);
      M.glass.color.set(o.glass || '#dfeef0');
      M.glass.opacity = new THREE.Color(o.glass || '#dfeef0').getHSL({}).l > 0.7 ? 0.14 : 0.42;
      M.glass.needsUpdate = true;
      applyMetal(M.handle, o.handle, '#c4a46b');
      applyMetal(M.gold, '#c9a14a');
    } else {
      applyWood(M, o.wood, redraw, assets.grain);
      applyFabric(M, o.fabric, redraw);
      applyTop(M, o.top, o.wood, redraw);
      applyMetal(M.accent, o.accent, '#c4a46b');
    }
    const k = mode === 'niche' ? [o.modules, o.crown, !!o.light].join('|') : [o.seats, o.shape, o.chair, !!o.accent].join('|');
    if (k !== structKey) {
      const first = !model;
      if (model) {
        scene.remove(model);
        dispose(model);
      }
      model = mode === 'niche' ? niche(o, M) : diningSet(o, M);
      scene.add(model);
      structKey = k;
      frame(!first);
    }
    redraw();
  }

  let w = 0, h = 0;
  function resize() {
    const el = renderer.domElement.parentElement;
    if (!el) return;
    const nw = el.clientWidth, nh = el.clientHeight;
    if (!nw || !nh || (nw === w && nh === h)) return;
    w = nw;
    h = nh;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    if (model) frame(true);
    redraw();
  }
  const ro = new ResizeObserver(resize);
  function setHost(el, interactive = true) {
    controls.enabled = interactive;
    if (renderer.domElement.parentElement === el) return;
    if (renderer.domElement.parentElement) ro.unobserve(renderer.domElement.parentElement);
    el.appendChild(renderer.domElement);
    ro.observe(el);
    w = h = 0;
    resize();
  }
  setHost(host);

  // render only when something changed (damping keeps it alive while moving)
  renderer.setAnimationLoop(() => {
    if (controls.update()) dirty = true;
    if (!dirty) return;
    dirty = false;
    renderer.render(scene, camera);
  });

  return {
    update,
    setHost,
    resetView: () => frame(false),
    snapshot: () => renderer.domElement.toDataURL('image/jpeg', 0.82),
    interact: (cb) => renderer.domElement.addEventListener('pointerdown', cb, { once: true }),
  };
}

function supported() {
  try {
    const c = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
  } catch (e) {
    return false;
  }
}

window.RonaqScene = { create, supported };
window.dispatchEvent(new Event('ronaq3d:ready'));
